import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Minus, Plus, Trash2 } from 'lucide-react';
import {
  ItemCarrinho,
  OBSERVACAO_ITEM_MAX_LEN,
  cartItemUnitSlots,
  countItensSelecionadosPagamento,
  itemIncluidoPagamento,
  normalizeItemObservacao,
  totalItem,
  totalSelecionadoPagamento,
  type CartUnitSelection,
} from '@caixa/types';
import { formatBRL } from '@caixa/lib/format';
import { numberToMoneyMask, parseMoneyMask } from '@caixa/lib/moneyMask';
import { MoneyInput } from '@caixa/components/MoneyInput';
import { groupCartQuantities } from '@caixa/lib/cartQuantities';

type DisplayRow = {
  key: string;
  item: ItemCarrinho;
  unitIndex: number | null;
  displayQty: number;
  lineTotal: number;
  marcado: boolean;
  sourceIds: string[];
};

interface CarrinhoProps {
  items: ItemCarrinho[];
  desagrupar: boolean;
  unitSelection: CartUnitSelection;
  onDesagruparChange: (value: boolean) => void;
  onUpdateQty: (id: string, delta: number, groupIds?: string[]) => void;
  onRemove: (id: string) => void;
  onUpdatePreco: (id: string, preco: number, unitIndex?: number | null) => void;
  onUpdateObservacao: (id: string, observacao: string) => void;
  onMarcadoChange: (id: string, marcado: boolean) => void;
  onUnitMarcadoChange: (id: string, unitIndex: number, marcado: boolean) => void;
}

export function Carrinho({
  items,
  desagrupar,
  unitSelection,
  onDesagruparChange,
  onUpdateQty,
  onRemove,
  onUpdatePreco,
  onUpdateObservacao,
  onMarcadoChange,
  onUnitMarcadoChange,
}: CarrinhoProps) {
  const totalItens = countItensSelecionadosPagamento(items, desagrupar, unitSelection);
  const totalGeral = totalSelecionadoPagamento(items, desagrupar, unitSelection);
  const [agora, setAgora] = useState(() => new Date());
  const [editingObsId, setEditingObsId] = useState<string | null>(null);
  const [editingObsDraft, setEditingObsDraft] = useState('');
  const editingObsOriginalRef = useRef('');

  useEffect(() => {
    const timer = window.setInterval(() => {
      setAgora(new Date());
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  const displayRows = useMemo((): DisplayRow[] => {
    const rows: DisplayRow[] = [];
    for (const { item, ids: sourceIds } of groupCartQuantities(items, desagrupar)) {
      const slots = cartItemUnitSlots(item);
      if (!desagrupar || slots === 1) {
        const marcado = desagrupar
          ? (unitSelection[item.id]?.[0] ?? itemIncluidoPagamento(item))
          : itemIncluidoPagamento(item);
        rows.push({
          key: item.id,
          item,
          unitIndex: desagrupar ? 0 : null,
          displayQty: item.quantidade,
          lineTotal: totalItem(item),
          marcado,
          sourceIds,
        });
        continue;
      }
      const flags = unitSelection[item.id] ?? Array.from({ length: slots }, () => itemIncluidoPagamento(item));
      const descontoUnit = item.quantidade > 0 ? item.desconto / item.quantidade : 0;
      for (let u = 0; u < slots; u++) {
        rows.push({
          key: `${item.id}:${u}`,
          item,
          unitIndex: u,
          displayQty: 1,
          lineTotal: item.precoUnitario - descontoUnit,
          marcado: flags[u] ?? false,
          sourceIds,
        });
      }
    }
    return rows;
  }, [items, desagrupar, unitSelection]);

  const beginEditObservacao = (item: ItemCarrinho) => {
    const current = item.observacao ?? '';
    editingObsOriginalRef.current = current;
    setEditingObsId(item.id);
    setEditingObsDraft(current);
  };

  const commitEditObservacao = (itemId: string) => {
    if (editingObsId !== itemId) return;
    const ids = displayRows.find((row) => row.item.id === itemId)?.sourceIds ?? [itemId];
    ids.forEach((id) => onUpdateObservacao(id, normalizeItemObservacao(editingObsDraft)));
    setEditingObsId(null);
    setEditingObsDraft('');
  };

  const cancelEditObservacao = () => {
    setEditingObsId(null);
    setEditingObsDraft('');
  };

  return (
    <div className="flex min-h-[280px] flex-col overflow-hidden rounded-lg border border-zinc-700 bg-[#242424] max-lg:h-auto max-lg:flex-none lg:h-full lg:min-h-0 lg:flex-1">
      <div className="flex items-center justify-between gap-3 border-b border-zinc-700 p-3">
        <label className="inline-flex cursor-pointer items-center gap-2 select-none">
          <button
            type="button"
            role="switch"
            aria-checked={desagrupar}
            aria-label="Desagrupar"
            onClick={() => onDesagruparChange(!desagrupar)}
            className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${
              desagrupar ? 'bg-white' : 'bg-[#333333]'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full shadow transition-transform ${
                desagrupar ? 'translate-x-4 bg-[#141414]' : 'translate-x-0 bg-white'
              }`}
            />
          </button>
          <span className={`text-sm ${desagrupar ? 'text-white' : 'text-zinc-300'}`}>Desagrupar</span>
        </label>
        <span className="text-zinc-400 text-sm">
          {agora.toLocaleString('pt-BR')}
        </span>
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        <div className="min-w-0 overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="bg-[#333333] sticky top-0">
            <tr className="text-zinc-300 text-left">
              <th className="w-10 whitespace-nowrap px-2 py-2 text-center font-medium" scope="col" aria-label="Incluir no pagamento">
                <span className="sr-only">Pagamento</span>
              </th>
              <th className="whitespace-nowrap px-2 py-2 font-medium">Descrição</th>
              <th className="w-16 whitespace-nowrap px-2 py-2 text-center font-medium">Qnt.</th>
              <th className="whitespace-nowrap px-2 py-2 font-medium">Preço (unit.)</th>
              <th className="whitespace-nowrap px-2 py-2 text-right font-medium">Total</th>
              <th className="w-20 whitespace-nowrap" />
            </tr>
          </thead>
          <tbody className="text-zinc-200">
            {items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-2 py-16 text-center text-zinc-500">
                  Nenhum item no carrinho
                </td>
              </tr>
            ) : (
              displayRows.map((row) => {
                const { item, unitIndex } = row;
                const splitMode = desagrupar && unitIndex != null && cartItemUnitSlots(item) > 1;
                const displayQty = item.permiteDecimal ? row.displayQty.toFixed(3) : row.displayQty;
                const obsText = normalizeItemObservacao(item.observacao);
                const showObsRow = obsText !== '' || editingObsId === item.id;
                return (
                <Fragment key={row.key}>
                  <tr className={showObsRow ? 'hover:bg-[#333333]/30' : 'border-b border-zinc-700/50 hover:bg-[#333333]/30'}>
                    <td className="px-2 py-1.5 text-center align-middle">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-zinc-500 bg-[#242424] text-emerald-500 focus:ring-emerald-500"
                        checked={row.marcado}
                        onChange={(e) => {
                          if (splitMode && unitIndex != null) {
                            onUnitMarcadoChange(item.id, unitIndex, e.target.checked);
                          } else {
                            row.sourceIds.forEach((id) => onMarcadoChange(id, e.target.checked));
                          }
                        }}
                        aria-label={`Incluir ${item.nome} no pagamento`}
                      />
                    </td>
                    <td className="px-2 py-1.5 max-w-[160px]" title={item.nome}>
                      <div>{item.nome}</div>
                      {item.itens_escolhidos && item.itens_escolhidos.length > 0 && (
                        <ul className="text-xs text-zinc-400 mt-0.5 list-none">
                          {item.itens_escolhidos.map((e, ei) => (
                            <li key={ei}>• {e.nome_produto}</li>
                          ))}
                        </ul>
                      )}
                    </td>
                  <td className="px-2 py-1.5 text-center">
                    {splitMode ? (
                      <span className="min-w-[1.5rem] inline-block">{displayQty}</span>
                    ) : (
                      <span className="inline-flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => onUpdateQty(item.id, -1, row.sourceIds)}
                          className="p-0.5 rounded-md hover:bg-[#333333] text-zinc-400 hover:text-white"
                          aria-label="Diminuir"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="min-w-[1.5rem] inline-block">{displayQty}</span>
                        <button
                          type="button"
                          onClick={() => onUpdateQty(item.id, 1, row.sourceIds)}
                          className="p-0.5 rounded-md hover:bg-[#333333] text-zinc-400 hover:text-white"
                          aria-label="Aumentar"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    <MoneyInput
                      value={numberToMoneyMask(item.precoUnitario)}
                      onChange={(v) =>
                        row.sourceIds.forEach((id) => onUpdatePreco(id, parseMoneyMask(v), splitMode ? unitIndex : null))
                      }
                      currencyPrefix
                      inputClassName="px-1.5 py-0.5 text-right"
                      className="caixa-money-input--compact w-20 rounded bg-[#242424] text-white border border-zinc-600 text-xs"
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right font-medium">
                    {formatBRL(row.lineTotal)}
                  </td>
                  <td className="px-2 py-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        if (splitMode) onUpdateQty(item.id, -1);
                        else row.sourceIds.forEach(onRemove);
                      }}
                      className="p-1 rounded-md hover:bg-red-600/30 text-zinc-400 hover:text-red-400"
                      aria-label={splitMode ? 'Remover unidade' : 'Remover'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
                {showObsRow ? (
                  <tr className="border-b border-zinc-700/50">
                    <td colSpan={6} className="px-2 pb-2 pt-0">
                      {editingObsId === item.id ? (
                        <textarea
                          autoFocus
                          rows={2}
                          maxLength={OBSERVACAO_ITEM_MAX_LEN}
                          value={editingObsDraft}
                          onChange={(e) =>
                            setEditingObsDraft(e.target.value.slice(0, OBSERVACAO_ITEM_MAX_LEN))
                          }
                          onBlur={() => commitEditObservacao(item.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              e.stopPropagation();
                              setEditingObsDraft(editingObsOriginalRef.current);
                              cancelEditObservacao();
                            }
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              commitEditObservacao(item.id);
                            }
                          }}
                          className="w-full resize-y rounded-md border border-zinc-600 bg-[#242424] px-2 py-1.5 text-xs text-zinc-200 outline-none focus:border-white focus:ring-1 focus:ring-white/80"
                          aria-label="Editar observação do item"
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => beginEditObservacao(item)}
                          className="block w-full whitespace-pre-wrap break-words text-left text-xs text-zinc-400 hover:text-zinc-200"
                        >
                          Observações: {obsText}
                        </button>
                      )}
                    </td>
                  </tr>
                ) : null}
                </Fragment>
              );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>

      <div className="p-4 border-t border-zinc-700 bg-[#333333] flex flex-wrap gap-6 items-end">
        <div>
          <div className="text-zinc-400 text-xs uppercase">ITENS</div>
          <div className="text-2xl font-bold text-white">{totalItens}</div>
        </div>
        <div className="ml-auto text-right">
          <div className="text-zinc-400 text-xs uppercase">TOTAL DOS PRODUTOS</div>
          <div className="text-2xl font-bold text-white">{formatBRL(totalGeral)}</div>
        </div>
      </div>
    </div>
  );
}
